import * as React from "react";
import {useCallback, useContext, useEffect} from "react";
import {Menu} from "semantic-ui-react";
import {ConfigContext} from "../../../context/ConfigContext";
import {backend} from "../../../../../xconvert-backend";
import {authentication} from "../../../../../authentication";
import {exportSimOutputIndex, ExportSimPrepareSide} from "../../converter/ExportSim/input/ExportSimPrepareSide";
import {QueuedEvent, ScriptErrorLogEntry, ScriptErrorLoggerRequest} from "../../../../../generated";
import {ExportSimOutputSide} from "../../converter/ExportSim/output/ExportSimOutputSide";
import ImportSimTabMenu from "../../converter/ImportSim/ImportSimTabMenu";
import {ScriptModifier} from "../configEdit/ScriptModifier";
import {Signal, signal} from "@preact/signals-react";
import {ImportSimOutputWithFileSelection} from "../../converter/ImportSim/ImportSimOutputWithFileSelection";
import {updateConfig, workingConfig} from "../ConfigSignal";
import {Splitter} from "../../../../util/table-splitter-ui/src/components/Splitters/Splitter";
import {ChangeableSplitter} from "../../../../util/table-splitter-ui/src/components/Splitters/ChangableSplitter";
import {queryLogs} from "../../logView/LogView";
import {sleep} from "../../../../../utils";
import {ScriptSelector} from "../AiScripter/ScriptSelector";
import {exportSimTransportOrders} from "../../converter/ExportSim/input/ExportSimTransportOrderEditor";


export const currentSelectedXRequestId: Signal<string> = signal("")
export const scriptConvPlaygroundScript: Signal<string> = signal("")
export const scriptConvPlaygroundScriptPath: Signal<string> = signal("")

export interface ScriptConverterPlaygroundProps {
    scriptPath: string
}

export function ScriptConverterPlayground(props: React.PropsWithChildren<ScriptConverterPlaygroundProps>) {
    const context = useContext(ConfigContext)

    const [auth, setAuth] = React.useState(null)
    const [selectedConverterSim, setSelectedConverterSim] = React.useState("Export")

    const [response, setResponse] = React.useState(null)
    const [queuedEvent, setQueuedEvent] = React.useState(null)
    const [config, setConfig] = React.useState(null)
    const [encoding, setEncoding] = React.useState(null)

    const [loading, setLoading] = React.useState(false)

    const logScriptError = useCallback(async (
        logEntry?: ScriptErrorLogEntry
    ) => {
        let auth = backend.withTokenAuthHeader(authentication.token)

        sleep(2000).then(async () => {
            queryLogs(60, logEntry.xRequestId).then(async r => {
                logEntry.logs = r
                let request = {} as ScriptErrorLoggerRequest
                request.entry = logEntry

                let response = await backend.internalApi.addScriptErrorLog(request, auth)
            })
        })
    }, [])

    const saveNewScript = useCallback((newScript) => {
        // console.log("saveNewScript: ", newScript) // full script on each key when directSave
        const jp = require('jsonpath');
        let config = JSON.parse(workingConfig.value)
        jp.value(config, scriptConvPlaygroundScriptPath.value, newScript)
        updateConfig(
            JSON.stringify(config, null, 4),
            true,
            false
        ).then(_ => {})
        scriptConvPlaygroundScript.value = newScript
    }, [])

    const beforeSend = useCallback((
        xRequestId: string,
    ) => {
        currentSelectedXRequestId.value = xRequestId
    }, [])

    const onSend = useCallback((
        response: any,
        queuedEvent: QueuedEvent,
        respondedConfig: any,
        encoding: string,
        logEntry?: ScriptErrorLogEntry
    ) => {
        setLoading(true)
        // console.log("onSend")
        setResponse(response)
        setQueuedEvent(JSON.stringify(queuedEvent, null, 4))
        setConfig(respondedConfig)
        setEncoding(encoding)
        // console.log("logEntry: ", logEntry)
        if (logEntry) {
            logScriptError(logEntry).then(r => {})
        }
        setLoading(false)
    }, [logScriptError])

    useEffect(() => {
        const handleTransfer = () => {
            const transferDataString = localStorage.getItem('scriptConverterPlaygroundTransfer');
            if (transferDataString) {
                localStorage.removeItem('scriptConverterPlaygroundTransfer');
                try {
                    const data = JSON.parse(transferDataString);

                    if (data.transportOrderJson) {
                        exportSimTransportOrders.value[exportSimOutputIndex.value] = JSON.parse(data.transportOrderJson);
                    }
                    if (data.scriptPath && typeof data.script === 'string') {
                        scriptConvPlaygroundScriptPath.value = data.scriptPath;
                        saveNewScript(data.script);
                    }

                    setSelectedConverterSim('Export');

                } catch (e) {
                    console.error("Failed to process transfer data from AI Scripter", e);
                }
            }
        };

        window.addEventListener('switchToScriptConverterPlayground', handleTransfer);
        handleTransfer();

        return () => {
            window.removeEventListener('switchToScriptConverterPlayground', handleTransfer);
        };
    }, [context, saveNewScript]);

    useEffect(() => {
        // console.log("ScriptConverterPlayground useEffect, scriptPath: ", props.scriptPath)

        if (props.scriptPath && props.scriptPath !== "") {
            scriptConvPlaygroundScriptPath.value = props.scriptPath
        } else if (!props.scriptPath) {
            scriptConvPlaygroundScriptPath.value = context.scriptConverterPlaygroundScriptPath
        }

        if (!auth) {
            setAuth(backend.withTokenAuthHeader(authentication.token))
        }
    }, [props.scriptPath])

    function converterSimSelector() {
        return <div style={{paddingBottom: 15, height: 42}}>
            <Menu>
                <Menu.Item
                    name='Export'
                    active={selectedConverterSim === 'Export'}
                    onClick={() => setSelectedConverterSim('Export')}
                >
                    Export
                </Menu.Item>
                <Menu.Item
                    name='Import'
                    active={selectedConverterSim === 'Import'}
                    onClick={() => setSelectedConverterSim('Import')}
                >
                    Import
                </Menu.Item>
            </Menu>
        </div>
    }

    function drawExportSimulatorInput() {
        return <ExportSimPrepareSide
            auth={auth}
            beforeSend={beforeSend}
            onSend={onSend}
        />
    }

    function drawImportSimulatorInput() {
        return <ImportSimTabMenu
            context={context}
            script={scriptConvPlaygroundScript.value}
            scriptPath={scriptConvPlaygroundScriptPath.value}
        />
    }

    function drawLeft() {
        return <div
            style={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100px",
                flex: "1",
            }}
        >
            {converterSimSelector()}
            {selectedConverterSim === "Export" && drawExportSimulatorInput()}
            {selectedConverterSim === "Import" && drawImportSimulatorInput()}
        </div>
    }

    function drawLeftTop() {


        return <>

            <ScriptSelector
                scriptPath={scriptConvPlaygroundScript.value}
                setScriptPath={(value) => scriptConvPlaygroundScriptPath.value = value}
                setScript={(value) => scriptConvPlaygroundScript.value = value}
                readOnly={false}
            />

            <ScriptModifier
                key="ScriptModifierFromScriptConverterPlayground"
                defaultDescription={null} //TODO: implement
                fieldDescriptionEnabled={true}
                readOnly={false}
                path={scriptConvPlaygroundScriptPath.value}
                dispatcher={saveNewScript}
                script={scriptConvPlaygroundScript.value}
                saveKeyListener={null}
                directSave={true}
            />
        </>
    }

    function drawExportSimOutput() {
        return <ExportSimOutputSide
            queuedEvent={queuedEvent}
            xRequestId={currentSelectedXRequestId.value}
            response={response}
            config={config}
            encoding={encoding}
            loading={loading}
        />
    }

    function drawRightBottom() {
        return <>
            {selectedConverterSim === "Export" && drawExportSimOutput()}
            {selectedConverterSim === "Import" && <ImportSimOutputWithFileSelection/>}
        </>
    }

    function drawRight() {
        return <ChangeableSplitter
            name={"scriptConverterPlaygroundRight"}
            topOrLeft={drawLeftTop()}
            bottomOrRight={drawRightBottom()}
        />
    }

    return <>
        <Splitter
            name={"scriptConverterPlaygroundSplitter"}
            left={drawLeft()}
            right={drawRight()}
        />
    </>
}